<script setup lang="ts">
/**
 * Card for displaying / selecting an Address on the Gesuch
 */
import { Gesuch } from '@/models/Gesuch'
import { formatTelefonNumber } from '@/helpers/tools.js'
import { Adresse, AdresseTyp } from '@/models/Adresse'
import ConfirmDlg from '@/components/confirm-dlg.vue'
import AdresseForm from '@/views/auth/adressen/AdressenForm.vue'
import AddressDlg from '@/components/gesuch-adresse-select-dlg.vue'
import { computed, onMounted, ref, type Ref } from 'vue'
import { useAuthUser } from '@/helpers/globals'
import { createModel } from '@kp/js-model'
import KpLink from '@/components/link.vue'
import { Data } from '@/models/apollon_data/Data'

type KontaktIdent = 'eigentuemer' | 'unternehmer' | 'kontakt' | 'bevollmaechtigter'

const {
    readOnly = false,
    gesuch,
    kontaktIdent,
    edit = false,
} = defineProps<{
    readOnly?: boolean
    gesuch: Gesuch
    kontaktIdent: KontaktIdent
    edit?: boolean
}>()

const loading = ref(false)
const orgAdresse: Ref<Adresse | null> = ref(null)
const showAddressDlg = ref(false)
const showAddAddressDlg = ref(false)
const showAddContactDlg = ref(false)
const confirmDelDlg = ref(false)
const newAddress: Ref<Adresse | null> = ref(null)
const newContact: Ref<Adresse | null> = ref(null)
const gesuchAdresse: Ref<Adresse | null> = ref(null)
const adresse: Ref<Adresse | null> = ref(null)
const unsavedAddress: Ref<Adresse | null> = ref(null)
const linkedBenutzerId: Ref<number | null> = ref(null)
const benutzer = useAuthUser()

const config = computed(() => {
    return gesuch.config?.config?.kontakte?.[kontaktIdent]
})

function canReassign() {
    if (readOnly) {
        return false
    }
    const adr = gesuch.data?.adressen?.[kontaktIdent]?.adresse
    if (adr && adr.benutzer_id === benutzer?.id) {
        const occurences = getGesuchAdressenBenutzerIds().filter((item) => item === benutzer?.id)
        return occurences.length > 1
    }
    return true
}

/**
 * Address can be removed only if (AND):
 * - the Gesuch is NOT read-only
 */
function canRemove() {
    return canReassign()
}

const emit = defineEmits<{
    (e: 'gesuchdatachanged', data: Data | null): void
}>()

function avoidAlterAdressType() {
    return kontaktIdent === 'unternehmer' || kontaktIdent === 'kontakt'
}

let data: Data | null = null
onMounted(() => {
    data = createModel(Data, gesuch.get('data'))
    adresse.value = data.adressen?.[kontaktIdent]?.adresse
        ? createModel(Adresse, data.adressen[kontaktIdent].adresse)
        : null
    linkedBenutzerId.value = data.adressen?.[kontaktIdent]?.adresse
        ? data.adressen[kontaktIdent].adresse.benutzer_id
        : null
    // If on mounting the edit flag is set to true, we open the address for editing:
    // then we open the requested address for editing:
    if (edit === true && adresse.value) {
        editAddress(adresse.value)
    }
})
function openAdressDlg() {
    unsavedAddress.value = adresse.value
    showAddressDlg.value = true
}
function closeAdressDlg() {
    showAddressDlg.value = false
}
async function assignAdresses(address: Adresse | null, contactAddress?: Adresse | null) {
    // Re-create the Data model from the current Gesuch state: the mount-time snapshot
    // would overwrite changes made by the other address cards since then.
    data = createModel(Data, gesuch.get('data'))
    if (address) {
        if (data?.adressen?.[kontaktIdent]) {
            data.adressen[kontaktIdent].set('adresse', address)
        }
        if (address.typ === AdresseTyp.ORGANISATION && contactAddress) {
            data?.adressen?.[kontaktIdent]?.adresse?.set('anrede', contactAddress.anrede)
            data?.adressen?.[kontaktIdent]?.adresse?.set('vorname', contactAddress.vorname)
            data?.adressen?.[kontaktIdent]?.adresse?.set('nachname', contactAddress.nachname)
            data?.adressen?.[kontaktIdent]?.adresse?.set('email', contactAddress.email)
            data?.adressen?.[kontaktIdent]?.adresse?.set('tel_1', contactAddress.tel_1)
            data?.adressen?.[kontaktIdent]?.adresse?.set('tel_2', contactAddress.tel_2)
            data?.adressen?.[kontaktIdent]?.adresse?.set('benutzer_id', contactAddress.benutzer_id)
        }
        adresse.value = data?.adressen?.[kontaktIdent]?.adresse || null
    }
    emit('gesuchdatachanged', data)
    closeAdressDlg()
}

function onNewAddress() {
    if (kontaktIdent === 'unternehmer') {
        newAddress.value = createModel(Adresse, { typ: AdresseTyp.ORGANISATION })
    } else {
        newAddress.value = createModel(Adresse, { typ: AdresseTyp.PERSON })
    }
    gesuchAdresse.value = null
    showAddAddressDlg.value = true
}

function onNewContact() {
    newContact.value = createModel(Adresse)
    newContact.value.set('typ', AdresseTyp.PERSON)
    showAddContactDlg.value = true
}

function onNewAddressClose(address: Adresse | null | undefined) {
    showAddAddressDlg.value = false
    if (address) {
        if (newAddress.value && newAddress.value.typ === AdresseTyp.ORGANISATION) {
            unsavedAddress.value = address
        } else {
            assignAdresses(address)
        }
    }
}

function onNewContactClose(contact: Adresse | null | undefined) {
    showAddContactDlg.value = false
    if (contact && orgAdresse.value) {
        assignAdresses(orgAdresse.value, contact)
    }
}

function replaceAdress() {
    openAdressDlg()
}

function editAddress(adresse: Adresse) {
    gesuchAdresse.value = adresse
    newAddress.value = null
    showAddAddressDlg.value = true
}

async function confirmDestroy() {
    // Same as in assignAdresses: work on the current Gesuch state, not the mount-time snapshot.
    data = createModel(Data, gesuch.get('data'))
    if (data?.adressen?.[kontaktIdent]) {
        data.adressen[kontaktIdent].adresse = null
        adresse.value = null
        emit('gesuchdatachanged', data)
        confirmDelDlg.value = false
    }
    loading.value = false
}

function getGesuchAdressenBenutzerIds() {
    const res = []
    if (gesuch.data?.adressen?.eigentuemer?.adresse) {
        res.push(gesuch.data.adressen.eigentuemer.adresse.benutzer_id)
    }
    if (gesuch.data?.adressen?.unternehmer?.adresse) {
        res.push(gesuch.data.adressen.unternehmer.adresse.benutzer_id)
    }
    if (gesuch.data?.adressen?.kontakt?.adresse) {
        res.push(gesuch.data.adressen.kontakt.adresse.benutzer_id)
    }
    if (gesuch.data?.adressen?.bevollmaechtigter?.adresse) {
        res.push(gesuch.data.adressen.bevollmaechtigter.adresse.benutzer_id)
    }
    return res
}

function onOrgSelected(org: Adresse | null) {
    orgAdresse.value = org
}
</script>

<template>
    <v-card tile height="260px" :loading="loading">
        <div v-if="adresse">
            <v-card-title v-if="adresse">
                <div class="title-container">
                    <div v-if="adresse.typ === AdresseTyp.ORGANISATION" class="name-container">
                        {{ adresse.organisation_name }}
                    </div>
                    <div v-else class="name-container">
                        {{ adresse.anrede }} {{ adresse.display_name }} {{ adresse.vorname }}
                        <br />{{ adresse.anrede2 }} {{ adresse.nachname2 }} {{ adresse.vorname2 }}
                    </div>
                    <v-spacer />
                    <v-btn
                        variant="text"
                        size="x-small"
                        color="primary"
                        :disabled="readOnly"
                        @click.stop="editAddress(adresse)"
                        icon="fas fa-pen"
                    >
                    </v-btn>
                    <v-btn
                        variant="text"
                        icon="fas fa-search"
                        size="x-small"
                        color="primary"
                        :disabled="!canReassign()"
                        @click.stop="replaceAdress"
                    >
                    </v-btn>
                    <v-btn
                        variant="text"
                        icon="fas fa-times"
                        size="x-small"
                        color="stop"
                        :disabled="!canRemove()"
                        @click.stop="confirmDelDlg = true"
                    >
                    </v-btn>
                </div>
            </v-card-title>
            <v-card-subtitle>{{ config?.label || 'LABEL' }}</v-card-subtitle>
            <v-card-text v-if="adresse">
                <div v-if="adresse.adresszusatz">{{ adresse.adresszusatz }}</div>
                <div v-if="adresse.typ === AdresseTyp.ORGANISATION">
                    {{ adresse.anrede }} {{ adresse.nachname }} {{ adresse.vorname }}
                </div>
                <div v-if="adresse.adresse">{{ adresse.adresse }}</div>
                <div v-if="adresse.postfach">{{ adresse.postfach }}</div>
                <div v-if="adresse.plz_ort">
                    {{
                        adresse.laendercode && adresse.laendercode !== 'CH'
                            ? adresse.laendercode + '-'
                            : ''
                    }}{{ adresse.plz_ort }}
                </div>
                <div v-if="adresse.tel_1">
                    <v-icon size="x-small" class="pr-1">fas fa-phone</v-icon>
                    <kp-link tel :href="adresse.tel_1">
                        {{ formatTelefonNumber(adresse.tel_1) }}
                    </kp-link>
                    {{ adresse.tel_2 ? '/' : '' }}
                    <kp-link v-if="adresse.tel_2" tel :href="adresse.tel_2">
                        {{ formatTelefonNumber(adresse.tel_2) }}
                    </kp-link>
                </div>
                <div v-else-if="adresse.organisation_tel_1">
                    <v-icon size="x-small" class="pr-1">fas fa-phone</v-icon>
                    <kp-link tel :href="adresse.organisation_tel_1">
                        {{ formatTelefonNumber(adresse.organisation_tel_1) }}
                    </kp-link>
                    {{ adresse.organisation_tel_2 ? '/' : '' }}
                    <kp-link
                        v-if="adresse.organisation_tel_2"
                        tel
                        :href="adresse.organisation_tel_2"
                    >
                        {{ formatTelefonNumber(adresse.organisation_tel_2) }}
                    </kp-link>
                </div>
                <div v-if="adresse.email && adresse.email !== ''">
                    <v-icon size="x-small" class="pr-1">fas fa-at</v-icon
                    ><kp-link mailto :href="adresse.email" />
                </div>
                <div v-else-if="adresse.organisation_email">
                    <v-icon size="x-small" class="pr-1">fas fa-at</v-icon
                    ><kp-link mailto :href="adresse.organisation_email" />
                </div>
            </v-card-text>
        </div>
        <div v-else style="margin-top: 1rem">
            <v-card-subtitle>{{ config?.label || 'LABEL' }}</v-card-subtitle>
            <v-card-text>
                <v-btn
                    color="action"
                    variant="flat"
                    :disabled="loading || readOnly"
                    @click="openAdressDlg"
                    prepend-icon="fas fa-plus"
                >
                    {{ config?.label || 'LABEL' }} {{ $t('assign') }}
                </v-btn>
                <div v-if="config?.hilfetext" class="d-flex mt-4">
                    <!-- eslint-disable-next-line vue/no-v-html -->
                    <div class="grey--text" v-html="config?.hilfetext" />
                </div>
            </v-card-text>
        </div>
        <AddressDlg
            v-if="showAddressDlg"
            v-model="showAddressDlg"
            :loading="loading"
            :ident="kontaktIdent"
            :gesuch="gesuch"
            :adresse="unsavedAddress"
            @close="closeAdressDlg"
            @assign="assignAdresses"
            @new-address="onNewAddress"
            @new-contact="onNewContact"
            @oganisation-selected="onOrgSelected"
        />
        <v-dialog v-if="showAddAddressDlg" v-model="showAddAddressDlg" max-width="1000" scrollable>
            <AdresseForm
                :new-address="newAddress"
                :gesuch-address="gesuchAdresse"
                :avoid-type-edit="avoidAlterAdressType()"
                :avoid-benutzer-id-edit="!canReassign()"
                show-in-dialog
                @close="onNewAddressClose"
            />
        </v-dialog>
        <v-dialog v-if="showAddContactDlg" v-model="showAddContactDlg" max-width="1000" scrollable>
            <AdresseForm
                :new-address="newContact"
                avoid-type-edit
                reduced-contact-data
                show-in-dialog
                @close="onNewContactClose"
            />
        </v-dialog>
        <ConfirmDlg
            v-model:model-value="confirmDelDlg"
            :title="$t('bestaetigung')"
            :text="$t('really_remove_address')"
            @yes="confirmDestroy"
            @no="confirmDelDlg = false"
        />
    </v-card>
</template>

<style lang="css" scoped>
.title-container {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
}
.name-container {
    max-width: 22rem;
    text-wrap: auto;
}
</style>
